import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Package, Users as UsersIcon, MessageCircle, HardDrive, Contact } from 'lucide-react';
import { licensingApi, isPlatformAdminRequiredError } from '@/api/licensing';
import { cn } from '@/utils/helpers';
import type { LimitKey } from '@/types/licensing';

const STATUS_BADGE: Record<string, string> = {
  active: 'bg-teal/15 text-teal border border-teal/30',
  trial: 'bg-amber-500/15 text-amber-400 border border-amber-500/30',
  suspended: 'bg-red-500/15 text-red-400 border border-red-500/30',
  cancelled: 'bg-navy-hover text-ink-muted border border-navy-border',
};

function tileColor(used: number, effective: number): string {
  if (effective === -1) return 'text-teal';
  if (effective === 0) return 'text-ink-secondary';
  const pct = (used / effective) * 100;
  if (pct > 90) return 'text-red-400';
  if (pct > 70) return 'text-amber-400';
  return 'text-teal';
}

function formatTile(used: number, effective: number, key: LimitKey): string {
  const usedLabel = key === 'storage_gb' ? `${used}GB` : String(used);
  if (effective === -1) return `${usedLabel} / Unlimited`;
  const effLabel = key === 'storage_gb' ? `${effective}GB` : String(effective);
  return `${usedLabel} / ${effLabel}`;
}

type Props = {
  orgId: string;
  manageHref?: string;
  onManageSubscription?: () => void;
};

export default function LicenseSummaryCard({ orgId, manageHref, onManageSubscription }: Props) {
  const { data: jumpstart } = useQuery({
    queryKey: ['admin-org-jumpstart-sub', orgId],
    queryFn: () => licensingApi.getOrgJumpstartSubscription(orgId),
    staleTime: 30_000,
  });

  const { data: limits, error: limitsError, isError: limitsIsError, isLoading: limitsLoading } = useQuery({
    queryKey: ['admin-org-effective-limits', orgId],
    queryFn: () => licensingApi.getEffectiveLimits(orgId),
    staleTime: 15_000,
    retry: false,
  });
  const limitsDenied = limitsIsError && isPlatformAdminRequiredError(limitsError);

  const TILES: { key: LimitKey; label: string; icon: typeof UsersIcon }[] = [
    { key: 'users', label: 'Users', icon: UsersIcon },
    { key: 'whatsapp_devices', label: 'WhatsApp', icon: MessageCircle },
    { key: 'storage_gb', label: 'Storage', icon: HardDrive },
    { key: 'contacts', label: 'Contacts', icon: Contact },
  ];

  return (
    <div className="mb-6 card p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Package className="h-5 w-5 text-indigo-400" />
          <div>
            <p className="text-xs uppercase tracking-wide text-ink-muted">License</p>
            <div className="mt-0.5 flex items-center gap-2">
              {jumpstart ? (
                <>
                  <span className="text-lg font-semibold text-ink-primary">
                    Jumpstart · {jumpstart.plan_code}
                  </span>
                  <span
                    className={cn(
                      'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase',
                      STATUS_BADGE[jumpstart.status] ?? STATUS_BADGE.cancelled
                    )}
                  >
                    {jumpstart.status}
                  </span>
                </>
              ) : (
                <span className="text-sm text-ink-muted">No jumpstart subscription</span>
              )}
            </div>
          </div>
        </div>
        {manageHref ? (
          <Link
            to={manageHref}
            className="rounded-lg border border-navy-border px-3 py-1.5 text-xs font-medium text-ink-secondary transition-colors hover:border-gold hover:text-gold"
          >
            Manage subscription
          </Link>
        ) : onManageSubscription ? (
          <button
            type="button"
            onClick={onManageSubscription}
            className="rounded-lg border border-navy-border px-3 py-1.5 text-xs font-medium text-ink-secondary transition-colors hover:border-gold hover:text-gold"
          >
            Manage subscription
          </button>
        ) : null}
      </div>

      {limitsDenied ? (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-300">
          Platform admin access required to view usage limits across organizations
          (is_platform_admin / is_super_admin).
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {TILES.map(({ key, label, icon: Icon }) => {
            const snap = limits?.[key];
            const used = snap?.used ?? 0;
            const effective = snap?.effective ?? 0;
            return (
              <div key={key} className="rounded-lg border border-navy-border bg-navy/40 p-3">
                <div className="flex items-center gap-1.5 text-xs text-ink-secondary">
                  <Icon className="h-3.5 w-3.5 text-indigo-400" />
                  {label}
                </div>
                <div className={cn('mt-1 num text-lg font-semibold', tileColor(used, effective))}>
                  {limitsLoading ? '…' : limits ? formatTile(used, effective, key) : '…'}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
